import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaIdCard, FaUser, FaPhone, FaEnvelope, FaChevronDown } from 'react-icons/fa';
import { ContactGrid, ContactRow, FormSection } from '../../styles';
import { CollapsibleHeader, CollapsibleTitle, CollapseIcon, CollapsibleContent, MissingText } from '../CollapsibleSection';
import { ContactFieldInput } from './ContactFieldInput';
import { ContactDesignationSelect } from './ContactDesignationSelect';
import { ContactGenderSelect } from './ContactGenderSelect';
import { ContactAddressFields } from './ContactAddressFields';
import { ContactTabs } from './ContactTabs';
import { ContactSectionProps, ContactFieldConfig } from './types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Configuration des champs de contact
 * Définit l'ordre, les icônes et la correspondance entre formData et contacts
 */
const ContactPreviewList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
`;

const ContactPreviewTag = styled.span`
  background-color: ${({ theme }) => theme.colors.backgroundHover};
  color: ${({ theme }) => theme.colors.text};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ theme }) => theme.colors.border};
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 3px 8px;
  }
`;

const contactFieldsConfig: ContactFieldConfig[] = [
  {
    key: 'name',
    contactKey: 'name',
    placeholder: 'Nom',
    icon: FaIdCard,
    type: 'text'
  },
  {
    key: 'firstName',
    contactKey: 'firstName',
    placeholder: 'Prénom',
    icon: FaUser,
    type: 'text'
  },
  {
    key: 'phone',
    contactKey: 'phone',
    placeholder: 'Téléphone',
    icon: FaPhone,
    type: 'tel'
  },
  {
    key: 'email',
    contactKey: 'email',
    placeholder: 'Email',
    icon: FaEnvelope,
    type: 'email'
  }
];

/**
 * Capitalise la première lettre d'une chaîne
 */
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Génère le label d'un contact pour l'aperçu
 */
const getContactPreviewLabel = (contact: { name?: string; firstName?: string }, _index: number): string | null => {
  const name = contact.name?.trim() || '';
  const firstName = contact.firstName?.trim() || '';
  
  if (name && firstName) {
    return `${capitalizeFirstLetter(name)} ${firstName.charAt(0).toUpperCase()}`;
  }
  if (name) {
    return capitalizeFirstLetter(name);
  }
  if (firstName) {
    return capitalizeFirstLetter(firstName);
  }
  
  return null; // Retourne null si aucune info
};

/**
 * Vérifie si un contact a des informations renseignées
 */
const hasContactInfo = (contact: { name?: string; firstName?: string }): boolean => {
  return !!(contact.name?.trim() || contact.firstName?.trim());
};

/**
 * Section Contact avec système d'onglets
 * - 1er contact obligatoire
 * - Maximum 3 contacts
 * - Suppression possible sauf le 1er
 * - Nommage : "Nom + 1ère lettre prénom"
 */
export const ContactSection: React.FC<ContactSectionProps> = ({
  formData,
  setFormData,
  errors,
  isReadOnly = false
}) => {
  const [activeContactIndex, setActiveContactIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // S'assurer qu'il y a toujours au moins un contact
  useEffect(() => {
    if (!formData.contacts || formData.contacts.length === 0) {
      const defaultContact = {
        id: uuidv4(),
        name: formData.name || '',
        firstName: formData.firstName || '',
        type: 'owner' as const,
        address: formData.contact_address || '',
        postalCode: '',
        city: '',
        phone: formData.phone || '',
        email: formData.email || '',
        designation: 'contact' as const
      };
      setFormData(prev => ({
        ...prev,
        contacts: [defaultContact]
      }));
    }
  }, []);
  
  // Ajouter un nouveau contact
  const handleAddContact = () => {
    if (formData.contacts && formData.contacts.length < 3) {
      const newContact = {
        id: uuidv4(),
        name: '',
        firstName: '',
        type: 'other' as const,
        address: '',
        postalCode: '',
        city: '',
        phone: '',
        email: '',
        designation: 'contact' as const
      };
      
      setFormData(prev => ({
        ...prev,
        contacts: [...(prev.contacts || []), newContact]
      }));
      
      // Basculer vers le nouvel onglet
      setActiveContactIndex((formData.contacts?.length || 0));
    }
  };
  
  // Supprimer un contact (sauf le 1er)
  const handleRemoveContact = (index: number) => {
    if (index === 0) return; // Ne pas supprimer le 1er contact
    
    setFormData(prev => {
      const updatedContacts = [...(prev.contacts || [])];
      updatedContacts.splice(index, 1);
      
      return {
        ...prev,
        contacts: updatedContacts
      };
    });
    
    // Revenir à l'onglet précédent si on supprime l'onglet actif
    if (activeContactIndex >= index) {
      setActiveContactIndex(Math.max(0, activeContactIndex - 1));
    }
  };
  
  // Mettre à jour le contact actif dans formData
  const updateActiveContact = (contactUpdates: any) => {
    setFormData(prev => {
      const updatedContacts = [...(prev.contacts || [])];
      if (updatedContacts[activeContactIndex]) {
        updatedContacts[activeContactIndex] = {
          ...updatedContacts[activeContactIndex],
          ...contactUpdates
        };
      }
      
      // Synchroniser avec les champs racine pour le 1er contact
      const formDataUpdates: any = { contacts: updatedContacts };
      if (activeContactIndex === 0 && updatedContacts[0]) {
        formDataUpdates.name = updatedContacts[0].name;
        formDataUpdates.firstName = updatedContacts[0].firstName;
        formDataUpdates.contact_address = updatedContacts[0].address;
        formDataUpdates.phone = updatedContacts[0].phone;
        formDataUpdates.email = updatedContacts[0].email;
      }
      
      return { ...prev, ...formDataUpdates };
    });
  };
  
  const activeContact = formData.contacts?.[activeContactIndex];
  
  return (
    <FormSection>
      <CollapsibleHeader onClick={() => setIsExpanded(!isExpanded)}>
        <CollapsibleTitle>Contact</CollapsibleTitle>
        <CollapseIcon $isExpanded={isExpanded}>
          <FaChevronDown />
        </CollapseIcon>
      </CollapsibleHeader>
      
      {!isExpanded && formData.contacts && formData.contacts.length > 0 && (
        formData.contacts.some(hasContactInfo) ? (
          <ContactPreviewList>
            {formData.contacts.map((contact, index) => {
              const label = getContactPreviewLabel(contact, index);
              return label ? (
                <ContactPreviewTag key={index}>
                  {label}
                </ContactPreviewTag>
              ) : null;
            })}
          </ContactPreviewList>
        ) : (
          <MissingText>Contact manquant</MissingText>
        )
      )}
      
      <CollapsibleContent $isExpanded={isExpanded}>
        <ContactGrid>
        {/* Ligne de désignation: Classification du contact */}
        <ContactRow>
          <ContactDesignationSelect
            formData={formData}
            setFormData={setFormData}
            error={errors.designation}
            isReadOnly={isReadOnly}
          />
        </ContactRow>
        
        {/* Onglets de contacts */}
        {formData.contacts && formData.contacts.length > 0 && (
          <ContactTabs
            contacts={formData.contacts}
            activeIndex={activeContactIndex}
            onTabChange={setActiveContactIndex}
            onAddContact={handleAddContact}
            onRemoveContact={handleRemoveContact}
            maxContacts={3}
          />
        )}
        
        {/* Première ligne: Civilité, Nom et Prénom */}
        <ContactRow>
          <ContactGenderSelect
            formData={formData}
            setFormData={setFormData}
            error={errors.gender}
            isReadOnly={isReadOnly}
            contactIndex={activeContactIndex}
          />
          <ContactFieldInput
            config={contactFieldsConfig[0]} // Nom
            formData={{...formData, name: activeContact?.name || ''}}
            setFormData={(updater) => {
              if (typeof updater === 'function') {
                const updated = updater({...formData, name: activeContact?.name || ''});
                updateActiveContact({ name: updated.name });
              }
            }}
            error={errors.name}
            isReadOnly={isReadOnly}
          />
          <ContactFieldInput
            config={contactFieldsConfig[1]} // Prénom
            formData={{...formData, firstName: activeContact?.firstName || ''}}
            setFormData={(updater) => {
              if (typeof updater === 'function') {
                const updated = updater({...formData, firstName: activeContact?.firstName || ''});
                updateActiveContact({ firstName: updated.firstName });
              }
            }}
            error={errors.firstName}
            isReadOnly={isReadOnly}
          />
        </ContactRow>
        
        {/* Deuxième et troisième lignes: Adresse avec autocomplétion, code postal et ville */}
        <ContactAddressFields
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          isReadOnly={isReadOnly}
          contactIndex={activeContactIndex}
        />
        
        {/* Quatrième ligne: Téléphone et Email */}
        <ContactRow>
          <ContactFieldInput
            config={contactFieldsConfig[2]} // Téléphone
            formData={{...formData, phone: activeContact?.phone || ''}}
            setFormData={(updater) => {
              if (typeof updater === 'function') {
                const updated = updater({...formData, phone: activeContact?.phone || ''});
                updateActiveContact({ phone: updated.phone });
              }
            }}
            error={errors.phone}
            isReadOnly={isReadOnly}
          />
          <ContactFieldInput
            config={contactFieldsConfig[3]} // Email
            formData={{...formData, email: activeContact?.email || ''}}
            setFormData={(updater) => {
              if (typeof updater === 'function') {
                const updated = updater({...formData, email: activeContact?.email || ''});
                updateActiveContact({ email: updated.email });
              }
            }}
            error={errors.email}
            isReadOnly={isReadOnly}
          />
        </ContactRow>
        </ContactGrid>
      </CollapsibleContent>
    </FormSection>
  );
};

